import React, { useState, useEffect, useContext } from "react";
import { logAnalyticsEvent, auth, db } from "../../firebase/firebaseConfig";
import { onSnapshot, doc, updateDoc } from "firebase/firestore";
import { reauthenticateWithCredential, EmailAuthProvider, updatePassword, signOut } from "firebase/auth";
import Header from "../../elements/Header";
import { EnvContext } from "../../context/EnvContext";
import Footer from "../../elements/Footer";
import { UserContext } from "../../context/UserContext";
import { StarRate, Delete } from "@mui/icons-material";
import ShopButton from "../../elements/ShopButton";
import { useNavigate } from "react-router-dom";
import Modal from "@mui/material/Modal";
import FAQ from "../../elements/FAQ";
import { Dialog, DialogActions, DialogTitle, Button } from "@mui/material";


const UserAccount = () => {
  const navigate = useNavigate();
  //
  const environment = useContext(EnvContext);
  //
  const [email, setEmail] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [addresses, setAddresses] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [addressToDeleteIndex, setAddressToDeleteIndex] = useState(null);
  const [showSignOutConfirmation, setShowSignOutConfirmation] = useState(false);

  const { user } = useContext(UserContext);

  const handleOpen = (index) => {
    setAddressToDeleteIndex(index);
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
    setAddressToDeleteIndex(null);
  };

  // Addresses
  useEffect(() => {
    if (!user) {
      console.log("User is not defined yet.");
      return;
    }

    const userRef = doc(db, `Users/UsersList/DataBase`, user?.uid);

    const unsubscribe = onSnapshot(userRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        setAddresses(data.addresses || []);
      } else {
        console.error("No document found!");
      }
    });

    return () => unsubscribe();
  }, [db, user]);

  // Delete address
  const handleDeleteAddress = async () => {
    if (addressToDeleteIndex === null) return;

    const userRef = doc(db, `Users/UsersList/DataBase`, user?.uid);
    const updatedAddresses = [...addresses];
    updatedAddresses.splice(addressToDeleteIndex, 1);

    try {
      await updateDoc(userRef, { addresses: updatedAddresses });
      handleClose(); // Close the modal after successful deletion
    } catch (error) {
      console.error("Error deleting address:", error);
      alert("Failed to delete address.");
    }
  };

  useEffect(() => {
    // Listen for changes in authentication state
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (user) {
        const userId = user.uid; // Get the authenticated user's ID
        const userDocRef = doc(db, "Users/UsersList/DataBase", userId); // Reference to the user document in Firestore

        const unsubscribeSnapshot = onSnapshot(
          userDocRef,
          (doc) => {
            if (doc.exists()) {
              const userData = doc.data();
              setEmail(userData.email || "");
            } else {
              console.error("No such document!");
            }
            setLoading(false);
          },
          (error) => {
            console.error("Error fetching user data: ", error);
            setLoading(false);
          }
        );

        // Clean up the Firestore listener when the component unmounts
        return () => unsubscribeSnapshot();
      } else {
        setLoading(false); // No authenticated user
      }
    });

    // Clean up the Auth listener when the component unmounts
    return () => unsubscribeAuth();
  }, []);

  const handleOldPasswordChange = (e) => {
    setOldPassword(e.target.value);
  };

  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value);
  };

  const handleConfirmNewPasswordChange = (e) => {
    setConfirmNewPassword(e.target.value);
  };

  // Handle setting a default address
  const handleSetDefaultAddress = async (index) => {
    const userRef = doc(db, `Users/UsersList/DataBase`, user?.uid);
    const updatedAddresses = [...addresses];
    const defaultAddress = updatedAddresses.splice(index, 1)[0];
    updatedAddresses.unshift(defaultAddress);

    await updateDoc(userRef, { addresses: updatedAddresses });
  };

  const handleChangePassword = async () => {
    if (!oldPassword || !newPassword || !confirmNewPassword) {
      alert("Please fill in all password fields.");
      return;
    }

    if (newPassword !== confirmNewPassword) {
      alert("New passwords do not match.");
      return;
    }

    const user = auth.currentUser;
    const credential = EmailAuthProvider.credential(user.email, oldPassword);

    try {
      // Reauthenticate user with old password
      await reauthenticateWithCredential(user, credential);
      // If reauthentication is successful, update the password
      await updatePassword(user, newPassword);
      alert("Password updated successfully.");
      // Clear password fields
      setOldPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (error) {
      if (error.code === "auth/wrong-password") {
        alert("Old password is incorrect.");
      } else if (error.code === "auth/weak-password") {
        alert("New password is too weak.");
      } else {
        console.error("Error changing password: ", error);
        alert("Failed to change password.");
      }
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>; // Show a loading indicator
  }

  return (
    <div style={{ ...styles.container }}>
      <Dialog open={showSignOutConfirmation} onClose={() => setShowSignOutConfirmation(false)}>
        <DialogTitle style={{ textAlign: "center" }}>Are you sure you want to sign out?</DialogTitle>
        <DialogActions>
          <div style={{ width: "100%", gap: 5, display: "flex", justifyContent: "center" }}>
            <Button onClick={() => setShowSignOutConfirmation(false)} variant="" sx={{ flex: 1, background: "black", color: "white", textAlign: "center" }}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                try {
                  await signOut(auth);
                  setShowSignOutConfirmation(false); // Close modal after signing out
                  navigate("/");
                } catch (error) {
                  console.error("Error signing out:", error);
                }
              }}
              color=""
              variant="outlined"
              style={{ flex: 1, textAlign: "center" }}
            >
              Confirm
            </Button>
          </div>
        </DialogActions>
      </Dialog>
      <Header search={false} />
      <div style={{ height: 35, borderBottom: "1px solid black", marginBottom: 20 }} />
      {/* Content */}
      <div style={styles.content}>
        {/** Email */}
        <div style={styles.inputGroup}>
          <div style={{ padding: "15px 0 10px 0" }}>
            <label>Email</label>
          </div>
          <div style={styles.emailDisplay}>
            <input type="text" id="email" value={email} disabled style={styles.input} />
          </div>
          <label htmlFor="username" style={styles.label}>
            Username
          </label>
        </div>

        {/** Addresses */}
        <div style={{ padding: "15px 0 0px 0" }}>
          <label>Saved Addresses</label>
        </div>

        <Modal open={openModal} onClose={handleClose} style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: 15 }}>
          <div style={styles.modal.container}>
            <div style={{ fontSize: "1.1rem", fontWeight: "600", paddingBottom: 10 }}>Delete this address?</div>
            <div style={{ paddingBottom: 20, fontSize: ".9rem" }}>This action cannot be undone.</div>
            <div style={{ ...styles.modal.buttonsContainer }}>
              <ShopButton text="Delete" theme="light" outlined buttonStyle={{ margin: 0 }} onClick={handleDeleteAddress} />
              <ShopButton text="Cancel" theme="dark" buttonStyle={{ margin: 0 }} onClick={handleClose} />
            </div>
          </div>
        </Modal>

        {addresses.map((element, index) => {
          let addressesTitle = null;

          return (
            <div key={index}>
              {addressesTitle}
              <div style={styles.address.container}>
                <div style={{}}>{element.address_name || "No Address"}</div>
                <div style={{}}>
                  {element.address
                    .split(",")
                    .filter((part) => part.trim() !== "United States")
                    .map((part, index) => (
                      <p key={index} style={styles.addressText}>
                        {part.trim()}
                      </p>
                    )) || ""}
                </div>
                <div style={{}}>{element.address_optional || ""}</div>
                <div
                  style={{
                    display: "flex",
                    alignContent: "space-between",
                    marginTop: 5,
                    flex: 1,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 5, flex: 1 }}>
                    <StarRate style={{ color: index == 0 ? "#FF9500" : "silver", fontSize: "18px" }} />
                    <div style={{ color: index == 0 ? "black" : "#3B72FE", height: 16, cursor: "pointer" }} onClick={() => handleSetDefaultAddress(index)}>
                      {index == 0 ? "Priority address" : "Make priority"}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center" }} onClick={() => handleOpen(index)}>
                    <Delete style={{ fontSize: 16 }} />
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        <div style={{ display: "flex", justifyContent: "center" }}>
          <ShopButton
            text="+ Add new address"
            theme="light"
            buttonStyle={{ margin: 0 }}
            outlined
            onClick={() => navigate("/useraddresses", { state: { userEmail: email } })}
          />
        </div>

        {/** Change Password */}
        <div style={{ padding: "15px 0 10px 0" }}>
          <label>Change Password</label>
        </div>
        <div style={styles.inputGroup}>
          <label htmlFor="oldPassword" style={styles.label}>
            Current Password
          </label>
          <input type="password" id="oldPassword" value={oldPassword} onChange={handleOldPasswordChange} style={styles.input} />

          <label htmlFor="newPassword" style={styles.label}>
            New Password (at least 6 characters)
          </label>
          <input type="password" id="newPassword" value={newPassword} onChange={handleNewPasswordChange} style={styles.input} />

          <label htmlFor="confirmNewPassword" style={styles.label}>
            Confirm New Password
          </label>
          <input type="password" id="confirmNewPassword" value={confirmNewPassword} onChange={handleConfirmNewPasswordChange} style={styles.input} />
        </div>

        <button style={{ ...styles.saveButton, borderRadius: 50 }} onClick={handleChangePassword}>
          Change Password
        </button>

        {user && (
          <>
            <div style={{ height: 100 }} />
            <div
              style={{}}
              onClick={() => {
                // analytics
                logAnalyticsEvent("menu_click", {
                  item: "Sign out",
                  environment: environment,
                });
                setShowSignOutConfirmation(true);
              }}
            >
              <Button color="" variant="outlined" css={{ borderRadius: "100px", fontWeight: 600 }}>Sign out</Button>
            </div>
          </>
        )}
        <FAQ />
      </div>
      <Footer />
    </div>
  );
};

// Inline styles specific to this page
const styles = {
  container: {
    margin: "0 auto",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    padding: "20px 20px 0",
    maxWidth: "1140px",
    margin: "0 auto"
  },
  inputGroup: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    marginBottom: "5px",
    color: "grey",
    fontSize: "14px",
  },
  input: {
    padding: "8px",
    fontSize: "16px",
    border: "1px solid #ccc",
    borderRadius: "4px",
  },
  emailDisplay: {
    display: "flex",
    flexDirection: "column",
  },
  emailText: {
    margin: "0",
    fontSize: "16px",
    color: "#000",
  },
  saveButton: {
    padding: "10px",
    fontSize: "16px",
    color: "white",
    backgroundColor: "black",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  loading: {
    textAlign: "center",
    fontSize: "18px",
    padding: "20px",
  },
  //
  address: {
    headerText: {
      fontSize: "1rem",
      fontWeight: "500",
      marginBottom: 10,
    },
    container: {
      border: "1px solid #D9D9D9",
      padding: "8px 8px",
      borderRadius: 8,
      fontSize: ".9rem",
    },
  },
  // modal
  modal: {
    container: {
      backgroundColor: "white",
      width: "100%",
      borderRadius: 8,
      padding: "30px 10px",
      justifyContent: "center",
      alignItems: "center",
      textAlign: "center",
    },
    buttonsContainer: {
      display: "flex",
      justifyContent: "center",
      marginTop: 15,
      gap: 20,
      width: "100%",
    },
  },
};

export default UserAccount;
